import React, { useState, useEffect, useRef } from "react";
import {
  Heart,
  Briefcase,
  DollarSign,
  Activity,
  Sparkles,
  ArrowLeft,
  KeyRound,
  X,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import TarotFanSpread from "./TarotFanSpread";
import { motion, AnimatePresence } from "framer-motion";
import { TarotCard, tarotCards } from "../../utils/tarotCards";
import BirthDetailsPopup from "../../utils/BirthDetailsPopup";
import axios from "axios";
import { api } from "../../services/api";
import remarkGfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import { Howl, Howler } from "howler";
import {
  qualifiesForSpecialMessage,
  getRandomSpecialMessage,
  getZodiacSign,
  parseDate,
} from "../../utils/birthdayMessages";
import SaveReading from "./SaveReading";
import { useTranslation } from "react-i18next";
import HealJaiTalkButton from "../common/HealJaiTalkButton";
import { useCategories } from "../../contexts/CategoryContext";

interface BirthDetails {
  dob: string;
  birthTime: string;
  birthPlace: string;
}

interface CardInsight {
  card_name: string;
  summary: string;
  guidance: string;
}

// Mystical Prayer Screen Component
const MysticalPrayerScreen = ({
  category,
  onPrayerComplete,
  onBack,
}: {
  category: string;
  onPrayerComplete: () => void;
  onBack: () => void;
}) => {
  const { t } = useTranslation() as any;
  const [pressProgress, setPressProgress] = useState(0);
  const [isPressing, setIsPressing] = useState(false);
  const pressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get category-specific mystical elements
  const getMysticalElements = (cat: string) => {
    switch (cat) {
      case "love":
        return {
          mantra: t("tarot.mystical.love.mantra"),
          chant: t("tarot.mystical.love.chant"),
          symbol: "🌸",
          color: "#E5B8AD",
          elements: ["❤️", "💕", "💗", "💖", "💘"],
          background: "from-pink-100/30 to-rose-100/30",
        };
      case "work":
        return {
          mantra: t("tarot.mystical.work.mantra"),
          chant: t("tarot.mystical.work.chant"),
          symbol: "🌟",
          color: "#8A7F73",
          elements: ["💼", "📈", "✨", "⭐", "🎯"],
          background: "from-amber-100/30 to-yellow-100/30",
        };
      case "money":
        return {
          mantra: t("tarot.mystical.money.mantra"),
          chant: t("tarot.mystical.money.chant"),
          symbol: "💰",
          color: "#B8A99A",
          elements: ["💰", "💎", "🪙", "💫", "🌾"],
          background: "from-emerald-100/30 to-teal-100/30",
        };
      case "healjai":
        return {
          mantra: t("tarot.mystical.healjai.mantra"),
          chant: t("tarot.mystical.healjai.chant"),
          symbol: "🌿",
          color: "#6D4C92",
          elements: ["🌿", "🕊️", "💚", "🌸", "✨"],
          background: "from-purple-100/30 to-indigo-100/30",
        };
      case "challenges":
        return {
          mantra: t("tarot.mystical.challenges.mantra"),
          chant: t("tarot.mystical.challenges.chant"),
          symbol: "⚡",
          color: "#B33F40",
          elements: ["⚡", "🔥", "🌪️", "💪", "🛡️"],
          background: "from-red-100/30 to-orange-100/30",
        };
      case "opportunities":
        return {
          mantra: t("tarot.mystical.opportunities.mantra"),
          chant: t("tarot.mystical.opportunities.chant"),
          symbol: "🔑",
          color: "#2E7D5E",
          elements: ["🔑", "🚪", "✨", "🌟", "🌄"],
          background: "from-green-100/30 to-emerald-100/30",
        };
      default:
        return {
          mantra: t("tarot.mystical.default.mantra"),
          chant: t("tarot.mystical.default.chant"),
          symbol: "🔮",
          color: "#6D4C92",
          elements: ["✨", "🌟", "💫", "⭐", "🔮"],
          background: "from-violet-100/30 to-fuchsia-100/30",
        };
    }
  };

  const mystical = getMysticalElements(category);

  const startPressing = () => {
    setIsPressing(true);
    setPressProgress(0);

    progressIntervalRef.current = setInterval(() => {
      setPressProgress((prev) => {
        if (prev >= 100) {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
          }
          return 100;
        }
        return prev + 1;
      });
    }, 30);

    pressTimerRef.current = setTimeout(() => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      onPrayerComplete();
    }, 3000);
  };

  const stopPressing = () => {
    setIsPressing(false);
    setPressProgress(0);
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const FloatingElements = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {mystical.elements.map((element, i) => (
        <motion.div
          key={i}
          initial={{
            x: Math.random() * 1000,
            y: Math.random() * 1000,
            opacity: 0,
          }}
          animate={{
            y: [null, -30, 30, -20, 20, 0],
            x: [null, 20, -20, 10, -10, 0],
            rotate: [0, 180, 360],
            opacity: [0, 1, 0.8, 1, 0],
          }}
          transition={{
            duration: 8 + Math.random() * 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.5,
          }}
          className="absolute text-4xl"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
        >
          {element}
        </motion.div>
      ))}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: `radial-gradient(circle at center, ${mystical.color}20, #F5F1EA)`,
      }}
    >
      <FloatingElements />

      <div className="relative z-10 max-w-md w-full mx-4">
        <motion.div
          initial={{ scale: 0.9, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring" }}
          className="relative bg-white/90 backdrop-blur-lg rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/50"
          style={{
            boxShadow: `0 25px 50px -12px ${mystical.color}60`,
          }}
        >
          <button
            onClick={onBack}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/80 backdrop-blur-sm border border-black/10 shadow hover:bg-white transition-all hover:scale-105"
            title={t("common.close")}
            aria-label={t("common.close")}
          >
            <X className="w-4 h-4 text-[#4A3F35]" />
          </button>

          <motion.div
            animate={{
              rotate: [0, 10, -10, 10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="text-5xl sm:text-7xl text-center mb-4 sm:mb-6"
          >
            {mystical.symbol}
          </motion.div>

          <h2 className="text-xl sm:text-2xl font-serif text-center text-[#4A3F35] mb-2">
            {category === "challenges"
              ? t("tarot.category.generalReading")
              : category === "opportunities"
                ? t("tarot.category.currentEnergyInsights")
                : t(`tarot.category.${category}`)}{" "}
            {t("tarot.readingText")}
          </h2>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center mb-4 sm:mb-6"
          >
            <p className="text-xs sm:text-sm text-[#6D4C92] font-medium tracking-wider uppercase">
              {t("tarot.mystical.sacredIntention")}
            </p>
            <p className="text-lg sm:text-xl font-serif text-[#4A3F35] mt-1 italic">
              "{mystical.mantra}"
            </p>
          </motion.div>

          <motion.div
            animate={{
              scale: [1, 1.02, 1],
              opacity: [0.7, 1, 0.7],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="text-center mb-6 sm:mb-8 p-3 sm:p-4 rounded-xl"
            style={{ backgroundColor: `${mystical.color}10` }}
          >
            <p className="text-base sm:text-lg italic text-[#4A3F35]">
              "{mystical.chant}"
            </p>
          </motion.div>

          <div className="space-y-4">
            <motion.button
              onMouseDown={startPressing}
              onMouseUp={stopPressing}
              onMouseLeave={stopPressing}
              onTouchStart={startPressing}
              onTouchEnd={stopPressing}
              className="relative w-full py-4 sm:py-5 px-4 sm:px-6 rounded-xl sm:rounded-2xl overflow-hidden group cursor-pointer select-none"
              style={{
                background: `linear-gradient(135deg, ${mystical.color}, ${mystical.color}dd)`,
                boxShadow: `0 10px 20px -5px ${mystical.color}80`,
              }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="absolute left-0 top-0 h-full bg-white/30"
                style={{ width: `${pressProgress}%` }}
                initial={{ width: "0%" }}
                animate={{ width: `${pressProgress}%` }}
                transition={{ duration: 0.1 }}
              />

              <div className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
                <span className="text-white text-lg sm:text-xl">🙏</span>
                <span className="text-white font-medium text-base sm:text-lg">
                  {isPressing
                    ? t("tarot.mystical.releasingIntentions")
                    : t("tarot.mystical.holdToSetIntention")}
                </span>
                <span className="text-white/80 text-xs sm:text-sm ml-1 sm:ml-2">
                  {!isPressing && t("tarot.mystical.holdDuration")}
                </span>
              </div>

              {isPressing && (
                <motion.div
                  initial={{ scale: 0, opacity: 0.5 }}
                  animate={{ scale: 2, opacity: 0 }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                  className="absolute inset-0 bg-white/30 rounded-full"
                />
              )}
            </motion.button>

            <motion.p
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-center text-xs sm:text-sm text-[#8A7F73]"
            >
              {t("tarot.mystical.holdInstruction")}
            </motion.p>
          </div>

          <div className="absolute -bottom-4 left-0 right-0 flex justify-center gap-2">
            {[1, 2, 3].map((i) => (
              <motion.div
                key={i}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: i * 0.3,
                }}
                className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full"
                style={{ backgroundColor: mystical.color }}
              />
            ))}
          </div>
        </motion.div>

        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -inset-4 -z-10 rounded-full blur-3xl"
          style={{ backgroundColor: mystical.color }}
        />
      </div>
    </motion.div>
  );
};

const TarotCardCategory = () => {
  const { t } = useTranslation() as any;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subCategoryId = searchParams.get("category");

  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  type Tab = "soul" | "current";
  const [activeTab, setActiveTab] = useState<Tab>("soul");
  const [showDetail, setShowDetail] = useState(false);
  const [selectedCards, setSelectedCards] = useState<TarotCard[]>([]);
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [aiCardInsights, setAiCardInsights] = useState<CardInsight[]>([]);
  const [revealedCardIndices, setRevealedCardIndices] = useState<Set<number>>(
    new Set(),
  );
  const [activeRevealIndex, setActiveRevealIndex] = useState<number | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [showPrayerScreen, setShowPrayerScreen] = useState(false);
  const [specialMessage, setSpecialMessage] = useState<string | null>(null);
  const [zodiacSign, setZodiacSign] = useState<string>("");
  const [userQuestion, setUserQuestion] = useState<string>("");
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  // Birth details state
  const [birthDetails, setBirthDetails] = useState<BirthDetails | null>(null);
  const [showBirthPopup, setShowBirthPopup] = useState(false);
  const [pendingCardSelection, setPendingCardSelection] = useState<
    string | null
  >(null);
  const { categories } = useCategories();
  const healJaiTalkCategory = categories.filter(
    (c) => c.name === "HealJai Talk" || c.name === "Astria Talk",
  );

  // Track window width for responsive calculations
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  useEffect(() => {
    const savedDetails = localStorage.getItem("userBirthDetails");
    if (savedDetails) {
      try {
        setBirthDetails(JSON.parse(savedDetails));
      } catch (e) {
        console.error("Failed to parse birth details");
      }
    }
  }, []);

  useEffect(() => {
    if (subCategoryId) {
      console.log("📋 SubCategory ID from URL:", subCategoryId);
    }
  }, [subCategoryId]);

  useEffect(() => {
    if (birthDetails?.dob) {
      const hasSpecial = qualifiesForSpecialMessage(birthDetails.dob);
      if (hasSpecial) {
        const message = getRandomSpecialMessage(birthDetails.dob);
        setSpecialMessage(message);

        // Get and set the zodiac sign
        const date = parseDate(birthDetails.dob);
        if (date) {
          const sign = getZodiacSign(date);
          setZodiacSign(sign);
        }
      } else {
        setSpecialMessage(null);
        setZodiacSign("");
      }
    }
  }, [birthDetails]);

  const colors = {
    amethyst: "#6D4C92",
    darkAmethyst: "#44304e",
    roseGold: "#E5B8AD",
    pearl: "#F8F4FF",
    textDark: "#4A3F35",
    textLight: "#8A7F73",
    border: "#E8E3DA",
    bgLight: "#FFFFFF",
  };

  const cardClass =
    "w-full rounded-[24px] sm:rounded-[28px] bg-white " +
    "border border-black/10 shadow-[0_10px_28px_rgba(0,0,0,0.08)] " +
    "px-5 sm:px-6 py-5 sm:py-6 text-left transition-all duration-300 " +
    "hover:-translate-y-1 hover:shadow-[0_18px_42px_rgba(0,0,0,0.12)] " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B8AD]/70";

  type CategoryCard = {
    id: string;
    title: string;
    subtitle: string;
    bullets: readonly string[];
  } & (
    | { emoji: string; Icon?: never }
    | { Icon: React.ComponentType<{ className?: string }>; emoji?: never }
  );

  const soulCategoryCards: readonly CategoryCard[] = [
    {
      id: "love",
      title: t("tarot.cards.love.title"),
      subtitle: t("tarot.cards.love.subtitle"),
      emoji: "❤️",
      bullets: [
        t("tarot.cards.love.bullet1"),
        t("tarot.cards.love.bullet2"),
        t("tarot.cards.love.bullet3"),
      ],
    },
    {
      id: "work",
      title: t("tarot.cards.work.title"),
      subtitle: t("tarot.cards.work.subtitle"),
      emoji: "💼",
      bullets: [
        t("tarot.cards.work.bullet1"),
        t("tarot.cards.work.bullet2"),
        t("tarot.cards.work.bullet3"),
      ],
    },
    {
      id: "money",
      title: t("tarot.cards.money.title"),
      subtitle: t("tarot.cards.money.subtitle"),
      emoji: "💰",
      bullets: [
        t("tarot.cards.money.bullet1"),
        t("tarot.cards.money.bullet2"),
        t("tarot.cards.money.bullet3"),
      ],
    },
    {
      id: "healjai",
      title: t("tarot.cards.healjai.title"),
      subtitle: t("tarot.cards.healjai.subtitle"),
      emoji: "🌿",
      bullets: [
        t("tarot.cards.healjai.bullet1"),
        t("tarot.cards.healjai.bullet2"),
        t("tarot.cards.healjai.bullet3"),
      ],
    },
  ] as const;

  const currentCategoryCards: readonly CategoryCard[] = [
    {
      id: "challenges",
      title: t("tarot.cards.challenges.title"),
      subtitle: t("tarot.cards.challenges.subtitle"),
      Icon: Activity,
      bullets: [
        t("tarot.cards.challenges.bullet1"),
        t("tarot.cards.challenges.bullet2"),
        t("tarot.cards.challenges.bullet3"),
      ],
    },
    {
      id: "opportunities",
      title: t("tarot.cards.opportunities.title"),
      subtitle: t("tarot.cards.opportunities.subtitle"),
      Icon: KeyRound,
      bullets: [
        t("tarot.cards.opportunities.bullet1"),
        t("tarot.cards.opportunities.bullet2"),
        t("tarot.cards.opportunities.bullet3"),
      ],
    },
  ] as const;

  const formatBirthDetailsForAPI = (details: BirthDetails): string => {
    let formatted = `${t("tarot.api.dob")}: ${details.dob}`;
    if (details.birthTime)
      formatted += `, ${t("tarot.api.time")}: ${details.birthTime}`;
    if (details.birthPlace)
      formatted += `, ${t("tarot.api.place")}: ${details.birthPlace}`;
    return formatted;
  };

  const getCategoryName = () => {
    switch (selectedCard) {
      case "love":
        return "love";
      case "work":
        return "work";
      case "money":
        return "money";
      case "healjai":
        return "healjai";
      case "challenges":
        return "challenges";
      case "opportunities":
        return "opportunities";
      default:
        return selectedCard || "";
    }
  };

  const extractJsonFromText = (text: string) => {
    const trimmed = text.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) return trimmed;

    const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    if (fenced?.[1]) return fenced[1].trim();

    const firstBrace = trimmed.indexOf("{");
    const lastBrace = trimmed.lastIndexOf("}");
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      return trimmed.slice(firstBrace, lastBrace + 1);
    }

    return trimmed;
  };

  const fetchCardInsights = async (
    cards: TarotCard[],
    question: string,
    categoryName: string,
    memoryString: string,
    userId: string | null,
  ) => {
    try {
      const insightsPayload = {
        userId,
        tarotCategoryName: categoryName,
        subCategoryId: subCategoryId,
        userMessage: t("tarot.api.insightsMessage", {
          defaultValue:
            'For these selected tarot cards and user question "{{question}}", return ONLY strict JSON in this format: {"cards":[{"card_name":"string","summary":"4-5 sentence interpretation","guidance":"1-2 sentence practical guidance"}]}. Keep card order exactly same as input cards.',
          question: question,
        }),
        memory: memoryString,
        question: question,
        selectedCards: cards.map((card) => ({
          card_id: card.card_id,
          card_name: card.card_name,
          isReversed: false,
        })),
      };

      const insightsResponse = await api.post(
        "/backend/tarotChat/create",
        insightsPayload,
      );

      const insightsMessage =
        insightsResponse.data?.data?.chats?.[0]?.aiResponse;
      if (!insightsMessage) {
        setAiCardInsights([]);
        return;
      }

      const parsed = JSON.parse(extractJsonFromText(insightsMessage));
      const cardsArray = Array.isArray(parsed) ? parsed : parsed?.cards;
      if (!Array.isArray(cardsArray)) {
        setAiCardInsights([]);
        return;
      }

      const normalized = cardsArray
        .map((entry: any) => ({
          card_name: String(entry?.card_name || ""),
          summary: String(entry?.summary || ""),
          guidance: String(entry?.guidance || ""),
        }))
        .filter((entry: CardInsight) => entry.card_name);

      setAiCardInsights(normalized);
      console.log("Index:", normalized);
    } catch (error) {
      console.warn(
        "Card insights JSON parsing/request failed, using fallback.",
      );
      setAiCardInsights([]);
    }
  };

  const callTarotAPI = async (cards: TarotCard[], question: string) => {
    if (!birthDetails) {
      console.error("Birth details missing");
      return;
    }

    setIsLoading(true);
    setAiResponse(null);
    setAiCardInsights([]);

    try {
      const memoryString = formatBirthDetailsForAPI(birthDetails);
      const userId = localStorage.getItem("userId");
      const categoryName = getCategoryName();

      const payload = {
        userId: userId,
        tarotCategoryName: categoryName,
        subCategoryId: subCategoryId,
        userMessage: t("tarot.api.readingMessage", {
          defaultValue:
            "Please provide a tarot reading for {{categoryName}} with {{count}} card(s) and also give answer of the question - {{question}}.",
          categoryName: categoryName,
          count: cards.length,
          question: question,
        }),
        memory: memoryString,
        question: question,
        selectedCards: cards.map((card) => ({
          card_id: card.card_id,
          card_name: card.card_name,
          isReversed: false,
        })),
      };

      console.log("📤 Sending to API:", payload);

      // const response = await api.post("/backend/tarotChat/create", payload);

      // console.log("📥 API Response:", response.data);

      if (true) {
        // const aiMessage = response.data.data.chats[0]?.aiResponse;
        // setAiResponse(aiMessage);
        await fetchCardInsights(
          cards,
          question,
          categoryName,
          memoryString,
          userId,
        );
      }
    } catch (error) {
      console.error("❌ API Error:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("Error details:", error.response.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = (cardType: string) => {
    if (!birthDetails) {
      setPendingCardSelection(cardType);
      setShowBirthPopup(true);
    } else {
      setSelectedCard(cardType);
      setShowPrayerScreen(true);
    }
  };

  const handlePrayerComplete = () => {
    setShowPrayerScreen(false);
    setShowDetail(true);
    console.log(`Selected: ${selectedCard}`);
  };

  const handlePrayerBack = () => {
    setShowPrayerScreen(false);
    setSelectedCard(null);
  };

  const proceedToCategory = (cardType: string) => {
    setSelectedCard(cardType);
    setShowPrayerScreen(true);
    console.log(`Selected: ${cardType}`);
  };

  const handleBirthDetailsSave = (details: BirthDetails) => {
    setBirthDetails(details);
    if (pendingCardSelection) {
      proceedToCategory(pendingCardSelection);
      setPendingCardSelection(null);
    }
  };

  const handleBirthPopupClose = () => {
    setShowBirthPopup(false);
    setPendingCardSelection(null);
  };

  const handleBackClick = () => {
    setShowDetail(false);
    setSelectedCard(null);
    setSelectedCards([]);
    setAiCardInsights([]);
    setRevealedCardIndices(new Set());
    setActiveRevealIndex(null);
    setCurrentCardIndex(0);
    setAiResponse(null);
  };

  const handleMainBackClick = () => {
    navigate("/");
  };

  const handleCardSelect = (clickedIndex: number) => {
    setActiveRevealIndex(clickedIndex);
  };

  const closeRevealModal = () => {
    if (activeRevealIndex !== null) {
      setRevealedCardIndices((prev) => {
        const next = new Set(prev);
        next.add(activeRevealIndex);
        return next;
      });
    }
    setActiveRevealIndex(null);
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleGetReading = async (cards: TarotCard[], question: string) => {
    const shuffledCards = shuffleArray(cards);
    setSelectedCards(shuffledCards);
    setAiCardInsights([]);
    setRevealedCardIndices(new Set());
    setActiveRevealIndex(null);
    setCurrentCardIndex(0);
    setUserQuestion(question);
    await callTarotAPI(shuffledCards, question);
  };

  const handleNextCard = () => {
    if (currentCardIndex < selectedCards.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
    }
  };

  const handlePrevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
    }
  };

  const getReadingText = (card: TarotCard) => {
    return t("tarot.reading.defaultText", {
      defaultValue:
        "{{cardName}} reveals powerful insight for your journey. Trust your intuition and move forward with confidence.",
      cardName: card.card_name,
    });
  };

  const getCardDescription = (card: TarotCard) => {
    if (!card) return "";

    const suitMatch = card.card_name.match(
      /of (Cups|Wands|Swords|Pentacles)$/i,
    );
    if (suitMatch) {
      const suit = suitMatch[1].toLowerCase();
      const suitMeaning: Record<string, string> = {
        cups: t("tarot.suit.cups"),
        wands: t("tarot.suit.wands"),
        swords: t("tarot.suit.swords"),
        pentacles: t("tarot.suit.pentacles"),
      };
      return t("tarot.suit.description", {
        defaultValue:
          "This card highlights {{suitMeaning}}. It suggests this theme is active right now and asking for honest attention. Stay calm, observe the patterns, and choose your next move with clarity.",
        suitMeaning: suitMeaning[suit],
      });
    }

    return t("tarot.card.defaultDescription", {
      defaultValue:
        "{{cardName}} points to a key turning point in your path. You may be entering a meaningful shift where awareness and timing matter. Trust your intuition, stay grounded, and move with intention in the next step.",
      cardName: card.card_name,
    });
  };

  const getDetailContent = () => {
    switch (selectedCard) {
      case "love":
        return {
          title: t("tarot.detail.love.title"),
          emoji: "❤️",
          icon: Heart,
          color: "#E5B8AD",
          paragraphs: [
            t("tarot.detail.love.p1"),
            t("tarot.detail.love.p2"),
            t("tarot.detail.love.p3"),
          ],
          footer: t("tarot.detail.love.footer"),
        };

      case "work":
        return {
          title: t("tarot.detail.work.title"),
          emoji: "💼",
          icon: Briefcase,
          color: "#8A7F73",
          paragraphs: [
            t("tarot.detail.work.p1"),
            t("tarot.detail.work.p2"),
            t("tarot.detail.work.p3"),
          ],
          footer: t("tarot.detail.work.footer"),
        };

      case "money":
        return {
          title: t("tarot.detail.money.title"),
          emoji: "💰",
          icon: DollarSign,
          color: "#B8A99A",
          paragraphs: [
            t("tarot.detail.money.p1"),
            t("tarot.detail.money.p2"),
            t("tarot.detail.money.p3"),
          ],
          footer: t("tarot.detail.money.footer"),
        };

      case "healjai":
        return {
          title: t("tarot.detail.healjai.title"),
          emoji: "🌿",
          icon: Activity,
          color: "#6D4C92",
          paragraphs: [
            t("tarot.detail.healjai.p1"),
            t("tarot.detail.healjai.p2"),
            t("tarot.detail.healjai.p3"),
          ],
          footer: t("tarot.detail.healjai.footer"),
        };

      case "challenges":
        return {
          title: t("tarot.detail.challenges.title"),
          emoji: "⚡",
          icon: Sparkles,
          color: "#B33F40",
          paragraphs: [
            t("tarot.detail.challenges.p1"),
            t("tarot.detail.challenges.p2"),
            t("tarot.detail.challenges.p3"),
          ],
          footer: t("tarot.detail.challenges.footer"),
        };

      case "opportunities":
        return {
          title: t("tarot.detail.opportunities.title"),
          emoji: "🔑",
          icon: Sparkles,
          color: "#2E7D5E",
          paragraphs: [
            t("tarot.detail.opportunities.p1"),
            t("tarot.detail.opportunities.p2"),
            t("tarot.detail.opportunities.p3"),
          ],
          footer: t("tarot.detail.opportunities.footer"),
        };

      default:
        return null;
    }
  };

  const detail = getDetailContent();
  const Icon = detail?.icon || Sparkles;
  type SpreadSlot = {
    x: number;
    y: number;
    label: string;
    meaning: string;
    rotate?: number;
    z?: number;
    labelPosition?: "top" | "bottom";
  };

  const celticSpreadSlots: readonly SpreadSlot[] = [
    {
      x: 35,
      y: 27,
      z: 10,
      label: t("tarot.spread.celtic.slot1"),
      meaning: t("tarot.spread.celtic.meaning1"),
    },
    {
      x: 35,
      y: 41,
      rotate: 90,
      labelPosition: "bottom",
      z: 20,
      label: t("tarot.spread.celtic.slot2"),
      meaning: t("tarot.spread.celtic.meaning2"),
    },
    {
      x: 35,
      y: 2,
      z: 10,
      label: t("tarot.spread.celtic.slot3"),
      meaning: t("tarot.spread.celtic.meaning3"),
    },
    {
      x: 58,
      y: 36,
      z: 10,
      label: t("tarot.spread.celtic.slot4"),
      meaning: t("tarot.spread.celtic.meaning4"),
    },
    {
      x: 35,
      y: 63,
      z: 10,
      label: t("tarot.spread.celtic.slot5"),
      meaning: t("tarot.spread.celtic.meaning5"),
    },
    {
      x: 12,
      y: 36,
      z: 10,
      label: t("tarot.spread.celtic.slot6"),
      meaning: t("tarot.spread.celtic.meaning6"),
    },
    {
      x: 86,
      y: 76,
      z: 10,
      label: t("tarot.spread.celtic.slot7"),
      meaning: t("tarot.spread.celtic.meaning7"),
    },
    {
      x: 86,
      y: 52,
      z: 10,
      label: t("tarot.spread.celtic.slot8"),
      meaning: t("tarot.spread.celtic.meaning8"),
    },
    {
      x: 86,
      y: 27,
      z: 10,
      label: t("tarot.spread.celtic.slot9"),
      meaning: t("tarot.spread.celtic.meaning9"),
    },
    {
      x: 86,
      y: 2,
      z: 10,
      label: t("tarot.spread.celtic.slot10"),
      meaning: t("tarot.spread.celtic.meaning10"),
    },
  ];

  // Current Energy Insights (10 cards) layout once all cards are revealed.
  // Matches the user's requested arrangement: 1-3 (top), 4-6 (middle),
  // 7-9 (bottom), 10 (right-middle).
  const currentEnergyInsightsSlots: readonly SpreadSlot[] = [
    {
      x: 24,
      y: 14,
      z: 10,
      label: t("tarot.spread.energy.slot1"),
      meaning: t("tarot.spread.energy.meaning1"),
    },
    {
      x: 44,
      y: 14,
      z: 10,
      label: t("tarot.spread.energy.slot2"),
      meaning: t("tarot.spread.energy.meaning2"),
    },
    {
      x: 64,
      y: 14,
      z: 10,
      label: t("tarot.spread.energy.slot3"),
      meaning: t("tarot.spread.energy.meaning3"),
    },
    {
      x: 24,
      y: 44,
      z: 10,
      label: t("tarot.spread.energy.slot4"),
      meaning: t("tarot.spread.energy.meaning4"),
    },
    {
      x: 44,
      y: 44,
      z: 10,
      label: t("tarot.spread.energy.slot5"),
      meaning: t("tarot.spread.energy.meaning5"),
    },
    {
      x: 64,
      y: 44,
      z: 10,
      label: t("tarot.spread.energy.slot6"),
      meaning: t("tarot.spread.energy.meaning6"),
    },
    {
      x: 24,
      y: 74,
      z: 10,
      label: t("tarot.spread.energy.slot7"),
      meaning: t("tarot.spread.energy.meaning7"),
    },
    {
      x: 44,
      y: 74,
      z: 10,
      label: t("tarot.spread.energy.slot8"),
      meaning: t("tarot.spread.energy.meaning8"),
    },
    {
      x: 64,
      y: 74,
      z: 10,
      label: t("tarot.spread.energy.slot9"),
      meaning: t("tarot.spread.energy.meaning9"),
    },
    {
      x: 80,
      y: 44,
      z: 10,
      label: t("tarot.spread.energy.slot10"),
      meaning: t("tarot.spread.energy.meaning10"),
    },
  ];
  const activeRevealCard =
    activeRevealIndex !== null ? selectedCards[activeRevealIndex] : null;
  const revealedCards = Array.from(revealedCardIndices)
    .sort((a, b) => a - b)
    .map((index) => ({
      index,
      card: selectedCards[index],
    }))
    .filter((entry) => entry.card);

  const isCurrentEnergyInsights = selectedCard === "opportunities";
  const spreadSlotsForTenCards = isCurrentEnergyInsights
    ? currentEnergyInsightsSlots
    : celticSpreadSlots;

  // Breathing Animation Loading Component with Single Sound
  const BreathingLoader = () => {
    const { t } = useTranslation() as any;
    const [breathingPhase, setBreathingPhase] = useState<"in" | "out">("in");
    const breathSound = useRef<Howl | null>(null);
    const [soundsLoaded, setSoundsLoaded] = useState(false);

    useEffect(() => {
      breathSound.current = new Howl({
        src: ["/cards/sounds/breath.mp3"],
        volume: 0.2,
        preload: true,
        html5: true,
        loop: false,
        onload: () => {
          console.log("✅ Breath sound loaded");
          setSoundsLoaded(true);
        },
        onloaderror: (id, error) => {
          console.warn("⚠️ Breath sound not available, using silent mode");
          setSoundsLoaded(true);
        },
      });

      return () => {
        breathSound.current?.unload();
      };
    }, []);

    useEffect(() => {
      if (!soundsLoaded || !breathSound.current) return;

      const playBreathSound = () => {
        if (!breathSound.current) return;
        breathSound.current.stop();
        const soundId = breathSound.current.play();
        if (breathSound.current) {
          const rate = breathingPhase === "in" ? 1.2 : 0.8;
          (breathSound.current as any).rate(rate, soundId);
        }
      };

      playBreathSound();

      const cycleInterval = setInterval(() => {
        setBreathingPhase((prev) => (prev === "in" ? "out" : "in"));
      }, 4000);

      return () => {
        clearInterval(cycleInterval);
        breathSound.current?.stop();
      };
    }, [soundsLoaded, breathingPhase]);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center justify-center py-8 sm:py-12 px-4"
      >
        <div className="relative flex items-center justify-center mb-6 sm:mb-8">
          <motion.div
            animate={{
              scale: breathingPhase === "in" ? [1, 1.3, 1] : [1, 1.1, 1],
              opacity:
                breathingPhase === "in" ? [0.3, 0.8, 0.3] : [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-[#B38EDB]/20 blur-xl"
          />

          <motion.div
            animate={{
              scale: breathingPhase === "in" ? [1, 1.2, 1] : [1, 1.1, 1],
              rotate: breathingPhase === "in" ? [0, 5, -5, 0] : [0, 2, -2, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="relative w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-[#6D4C92] to-[#44304e] flex items-center justify-center shadow-xl"
          >
            <motion.div
              animate={{
                rotateY: [0, 180, 360],
                scale: breathingPhase === "in" ? [1, 1.1, 1] : [1, 1.05, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                transformStyle: "preserve-3d",
              }}
            >
              <span className="text-white text-3xl sm:text-5xl">🔮</span>
            </motion.div>
          </motion.div>
        </div>

        <motion.div className="text-center space-y-3 sm:space-y-4">
          <motion.p
            animate={{
              scale: breathingPhase === "in" ? [1, 1.05, 1] : [1, 1.02, 1],
              opacity:
                breathingPhase === "in" ? [0.8, 1, 0.8] : [0.7, 0.9, 0.7],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="text-[#4A3F35] text-xl sm:text-2xl font-serif"
          >
            {t("tarot.loading.closeEyes")}
          </motion.p>

          <motion.p
            animate={{
              scale: [1, 1.03, 1],
              opacity: [0.7, 1, 0.7],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5,
            }}
            className="text-[#6D4C92] text-lg sm:text-xl"
          >
            {t("tarot.loading.thinkQuestion")}
          </motion.p>

          <motion.p
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            className="text-[#8A7F73] text-xs sm:text-sm italic mt-4 sm:mt-6"
          >
            {t("tarot.loading.universeListening")}
          </motion.p>
        </motion.div>

        <div className="flex items-center gap-1 mt-6 sm:mt-8 h-6 sm:h-8">
          {[1, 2, 3, 4, 5, 4, 3, 2, 1].map((height, i) => (
            <motion.div
              key={i}
              animate={{
                height:
                  breathingPhase === "in"
                    ? [height * 3, height * 6, height * 3]
                    : [height * 3, height * 4.5, height * 3],
                backgroundColor:
                  breathingPhase === "in"
                    ? ["#6D4C92", "#B38EDB", "#6D4C92"]
                    : ["#8A7F73", "#B8A99A", "#8A7F73"],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.1,
              }}
              className="w-0.5 sm:w-1 rounded-full"
              style={{ height: `${height * 3}px` }}
            />
          ))}
        </div>

        <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              animate={{
                scale: breathingPhase === "in" ? [1, 1.6, 1] : [1, 1.3, 1],
                opacity:
                  breathingPhase === "in" ? [0.5, 1, 0.5] : [0.4, 0.8, 0.4],
                backgroundColor:
                  breathingPhase === "in"
                    ? ["#6D4C92", "#B38EDB", "#6D4C92"]
                    : ["#8A7F73", "#B8A99A", "#8A7F73"],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.3,
              }}
              className="w-2 h-2 sm:w-3 sm:h-3 rounded-full"
            />
          ))}
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <AnimatePresence>
        {showPrayerScreen && selectedCard && (
          <MysticalPrayerScreen
            category={selectedCard}
            onPrayerComplete={handlePrayerComplete}
            onBack={handlePrayerBack}
          />
        )}
      </AnimatePresence>

      {showDetail && detail && selectedCards.length > 0 ? (
        <AnimatePresence mode="wait">
          <motion.div
            key="reading-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col min-h-screen bg-gradient-to-b from-[#F5F1EA] to-[#EAE5DB]"
          >
            <div className="top-0 z-10">
              <motion.div className="w-full bg-white/80 backdrop-blur-sm border-b border-[#E8E3DA]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
                  <div className="flex items-center gap-2 sm:gap-4">
                    {isMobile && (
                      <div className="inline-flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 rounded-full transition-all hover:scale-105 flex-shrink-0"></div>
                    )}

                    <div
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-md flex-shrink-0"
                      style={{
                        backgroundColor: detail.color,
                        color: "white",
                      }}
                    >
                      {detail.emoji ? (
                        <span className="text-lg leading-none">
                          {detail.emoji}
                        </span>
                      ) : (
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h1 className="text-sm sm:text-base md:text-xl font-medium text-[#4A3F35] truncate">
                        {detail.title}
                      </h1>
                      {selectedCards.length > 1 && (
                        <p className="text-xs text-[#8A7F73] mt-0.5 hidden sm:block">
                          {t("tarot.cardSpread.journeySpread", {
                            defaultValue: "{{count}}-Card Journey Spread",
                            count: selectedCards.length,
                          })}
                        </p>
                      )}
                    </div>

                    <Link
                      to="/tarotCard"
                      onClick={handleBackClick}
                      className="inline-flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-105 flex-shrink-0"
                      title={t("tarot.navigation.backToCategories")}
                    >
                      <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-[#4A3F35]" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="w-full px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 lg:py-10">
              <div className="flex flex-col gap-5 sm:gap-7">
                {/* Cards Container */}
                <div className="w-full flex justify-center">
                  <div className="w-full max-w-[1220px] rounded-2xl sm:rounded-3xl bg-white/75 border border-[#E8E3DA] shadow-md px-3 sm:px-5 md:px-8 py-3 sm:py-4">
                    <p className="text-sm sm:text-base font-medium text-[#4A3F35] mb-2">
                      {t("tarot.cardSpread.title", {
                        defaultValue: "{{count}} Card Reveal Spread",
                        count: selectedCards.length,
                      })}
                    </p>

                    {isLoading ? (
                      <BreathingLoader />
                    ) : (
                      <>
                        {selectedCards.length === 10 ? (
                          <div
                            className={`relative w-full rounded-xl sm:rounded-2xl border border-[#E8E3DA] bg-[#F6F2FA] overflow-hidden ${
                              isMobile
                                ? "h-[640px]"
                                : "h-[460px] sm:h-[640px] md:h-[740px]"
                            }`}
                          >
                            {spreadSlotsForTenCards.map((slot, index) => {
                              const card = selectedCards[index];
                              const isRevealed = revealedCardIndices.has(index);
                              const safeWindowWidth = windowWidth || 360;
                              const availableWidth = Math.max(
                                280,
                                Math.min(safeWindowWidth, 420) - 56,
                              );
                              const mobileCardWidth = Math.max(
                                44,
                                Math.min(56, Math.round(availableWidth * 0.14)),
                              );
                              const baseWidth = isMobile
                                ? mobileCardWidth
                                : isTablet
                                  ? 84
                                  : 96;
                              const baseHeight = isMobile
                                ? Math.round(mobileCardWidth * 1.66)
                                : isTablet
                                  ? 136
                                  : 158;
                              const rotate = slot.rotate ?? 0;
                              const mobileShiftX = isMobile ? 5 : 0;
                              const layoutShiftX =
                                isCurrentEnergyInsights &&
                                selectedCards.length === 10
                                  ? isMobile
                                    ? -6
                                    : -4
                                  : 0;
                              const effectiveX = isMobile
                                ? Math.min(
                                    95,
                                    Math.max(5, slot.x - mobileShiftX),
                                  )
                                : slot.x;
                              const shiftedX = Math.min(
                                95,
                                Math.max(5, effectiveX + layoutShiftX),
                              );
                              const effectiveY = isMobile
                                ? Math.max(slot.y, 8)
                                : slot.y;
                              if (!card) return null;
                              return (
                                <motion.div
                                  key={`${card.card_id}-${index}`}
                                  className="absolute"
                                  style={{
                                    left: `${shiftedX}%`,
                                    top: `${effectiveY}%`,
                                    transform: "translate(-50%, -50%)",
                                    zIndex: slot.z ?? 20 + index,
                                  }}
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{
                                    duration: 0.35,
                                    delay: index * 0.06,
                                  }}
                                  onClick={() => handleCardSelect(index)}
                                >
                                  {slot.labelPosition !== "bottom" && (
                                    <p className="text-[9px] sm:text-xs text-[#5D5565] text-center mb-1 leading-tight px-1 max-w-[140px] whitespace-pre-line">
                                      {slot.label}
                                    </p>
                                  )}
                                  <div
                                    className="relative rounded-lg sm:rounded-xl overflow-hidden ring-1 ring-[#6D4C92]/25 shadow-md"
                                    style={{
                                      width: baseWidth,
                                      height: baseHeight,
                                      transform:
                                        rotate !== 0
                                          ? `rotate(${rotate}deg)`
                                          : undefined,
                                      transformOrigin: "center",
                                    }}
                                  >
                                    {isRevealed ? (
                                      <img
                                        src={card.image_url}
                                        alt={card.card_name}
                                        className="w-full h-full object-contain bg-white"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gradient-to-br from-[#4A1F5E] via-[#3B1A4A] to-[#1E0C25] flex items-center justify-center">
                                        <div
                                          style={{
                                            width: "50%",
                                            height: "50%",
                                            border:
                                              "1px solid rgba(255,255,255,0.45)",
                                            transform: "rotate(45deg)",
                                          }}
                                          className="flex items-center justify-center"
                                        >
                                          <div
                                            style={{
                                              width: "34%",
                                              height: "34%",
                                              border:
                                                "1px solid rgba(255,255,255,0.5)",
                                              transform: "rotate(45deg)",
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {slot.labelPosition === "bottom" && (
                                    <p
                                      className={`text-[9px] sm:text-xs text-[#5D5565] text-center leading-tight px-1 max-w-[140px] whitespace-pre-line ${
                                        isMobile
                                          ? "mt-1"
                                          : "mt-[-18px] sm:mt-[-25px]"
                                      }`}
                                    >
                                      {slot.label}
                                    </p>
                                  )}
                                  {isRevealed && (
                                    <p className="text-[10px] sm:text-[11px] text-[#4A3F35] text-center mt-1 max-w-[130px] truncate">
                                      {/* {card.card_name} */}
                                    </p>
                                  )}
                                </motion.div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                            {selectedCards.map((card, index) => (
                              <div
                                key={`${card.card_id}-${index}`}
                                className="flex flex-col items-center"
                              >
                                <p className="text-[10px] sm:text-xs text-[#5D5565] mb-1">
                                  {t("tarot.cardCount")} {index + 1}
                                </p>
                                <div
                                  className="rounded-lg sm:rounded-xl overflow-hidden ring-1 ring-[#6D4C92]/25 shadow-md"
                                  onClick={() => handleCardSelect(index)}
                                >
                                  {revealedCardIndices.has(index) ? (
                                    <img
                                      src={card.image_url}
                                      alt={card.card_name}
                                      className="w-[90px] sm:w-[110px] md:w-[130px] h-[150px] sm:h-[180px] md:h-[220px] object-contain bg-white"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="w-[90px] sm:w-[110px] md:w-[130px] h-[150px] sm:h-[180px] md:h-[220px] bg-gradient-to-br from-[#4A1F5E] via-[#3B1A4A] to-[#1E0C25] flex items-center justify-center">
                                      <div
                                        style={{
                                          width: "50%",
                                          height: "50%",
                                          border:
                                            "1px solid rgba(255,255,255,0.45)",
                                          transform: "rotate(45deg)",
                                        }}
                                        className="flex items-center justify-center"
                                      >
                                        <div
                                          style={{
                                            width: "34%",
                                            height: "34%",
                                            border:
                                              "1px solid rgba(255,255,255,0.5)",
                                            transform: "rotate(45deg)",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {revealedCardIndices.has(index) && (
                                  <p className="text-[10px] sm:text-[11px] text-[#4A3F35] text-center mt-1 max-w-[130px] truncate">
                                    {`${card.card_name_th} (${card.card_name})`}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    <div className="mt-4 sm:mt-5 rounded-xl border border-[#E8E3DA] bg-white/80 p-3 sm:p-4">
                      <p className="text-xs sm:text-sm font-medium text-[#4A3F35]">
                        {t("tarot.openedCards", {
                          defaultValue: "Opened Cards ({{count}}/{{total}})",
                          count: revealedCards.length,
                          total: selectedCards.length,
                        })}
                      </p>
                      {revealedCards.length === 0 ? (
                        <p className="text-xs sm:text-sm text-[#8A7F73] mt-2">
                          {t("tarot.tapAnyCard")}
                        </p>
                      ) : (
                        <div className="mt-3 space-y-4">
                          {revealedCards.map(({ index, card }) => (
                            <div
                              key={`${card.card_id}-${index}-revealed`}
                              className="rounded-xl border border-[#6D4C92]/20 bg-[#F8F4FF] p-3 sm:p-4"
                            >
                              <div className="flex gap-3 sm:gap-4">
                                <img
                                  src={card.image_url}
                                  alt={card.card_name}
                                  className="w-[72px] sm:w-[94px] h-[120px] sm:h-[150px] object-contain rounded-lg border border-[#E8E3DA] bg-white flex-shrink-0"
                                  loading="lazy"
                                />
                                <div className="min-w-0">
                                  <p className="text-xs sm:text-sm text-[#4A3F35]">
                                    {selectedCards.length === 10
                                      ? spreadSlotsForTenCards[index]?.label ||
                                        `${t("tarot.cardCount")} ${index + 1}`
                                      : `${t("tarot.cardCount")} ${index + 1}`}
                                  </p>
                                  {selectedCards.length === 10 &&
                                    spreadSlotsForTenCards[index]?.meaning && (
                                      <p className="text-[11px] sm:text-xs text-[#8A7F73] mt-1 leading-relaxed">
                                        {spreadSlotsForTenCards[index].meaning}
                                      </p>
                                    )}
                                  <h4 className="text-sm sm:text-lg font-semibold text-[#2D2733] uppercase tracking-wide mt-1">
                                    {`${card.card_name} (${card.card_name_th})`}
                                  </h4>
                                  <div className="text-xs sm:text-sm text-[#5D5565] mt-2 leading-relaxed">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {aiCardInsights[index]?.summary}
                                    </ReactMarkdown>
                                  </div>
                                  <div className="text-xs sm:text-sm text-[#4A3F35] mt-2 leading-relaxed">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {aiCardInsights[index]?.guidance}
                                    </ReactMarkdown>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Reading Content */}
              </div>
            </div>

            <AnimatePresence>
              {activeRevealCard && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 bg-black/55 backdrop-blur-[1px] flex items-center justify-center p-4"
                  onClick={closeRevealModal}
                >
                  <motion.div
                    initial={{ scale: 0.92, opacity: 0, y: 18 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.92, opacity: 0, y: 18 }}
                    transition={{ duration: 0.22 }}
                    className="w-full max-w-3xl rounded-2xl sm:rounded-3xl bg-white shadow-2xl border border-[#E8E3DA] p-4 sm:p-6 md:p-7"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-5">
                      <img
                        src={activeRevealCard.image_url}
                        alt={activeRevealCard.card_name}
                        className="w-[116px] sm:w-[150px] h-[182px] sm:h-[230px] object-contain rounded-lg sm:rounded-xl border border-[#E8E3DA] bg-white"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-[#4A3F35]">
                          {activeRevealIndex !== null &&
                          selectedCards.length === 10
                            ? spreadSlotsForTenCards[activeRevealIndex]?.label
                            : `${t("tarot.cardCount")} ${activeRevealIndex !== null ? activeRevealIndex + 1 : ""}`}
                        </p>
                        {activeRevealIndex !== null &&
                          selectedCards.length === 10 &&
                          spreadSlotsForTenCards[activeRevealIndex]
                            ?.meaning && (
                            <p className="text-xs sm:text-sm text-[#8A7F73] mt-1 leading-relaxed">
                              {
                                spreadSlotsForTenCards[activeRevealIndex]
                                  .meaning
                              }
                            </p>
                          )}
                        <h3 className="text-lg sm:text-2xl font-semibold text-[#4A3F35] leading-tight mt-1">
                          {`${activeRevealCard.card_name} (${activeRevealCard.card_name_th})`}
                        </h3>
                        <div className="text-sm sm:text-base text-[#5D5565] mt-3 leading-relaxed">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {(activeRevealIndex !== null &&
                              aiCardInsights[activeRevealIndex]?.summary) ||
                              getCardDescription(activeRevealCard)}
                          </ReactMarkdown>
                        </div>
                        <div className="text-sm sm:text-base text-[#4A3F35] mt-3 leading-relaxed">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {(activeRevealIndex !== null &&
                              aiCardInsights[activeRevealIndex]?.guidance) ||
                              getReadingText(activeRevealCard)}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={closeRevealModal}
                      className="mt-5 w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-[#6D4C92] to-[#44304e] text-white py-3 text-sm sm:text-base font-medium hover:opacity-95 transition-opacity"
                    >
                      {t("tarot.closeAndReveal")}
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      ) : showDetail && detail ? (
        <div className="flex flex-col min-h-screen bg-[#F5F1EA]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 w-full">
            <div className="mb-6 sm:mb-8 mt-[30px] sm:mt-[40px]">
              <div className="flex justify-between items-center border-b border-[#E8E3DA] gap-2 sm:gap-3 pb-2 sm:pb-3">
                <div className="flex gap-2 sm:gap-3 pb-2 sm:pb-3">
                  <div
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${detail.color}20`,
                      color: detail.color,
                    }}
                  >
                    {detail.emoji ? (
                      <span className="text-lg leading-none">
                        {detail.emoji}
                      </span>
                    ) : (
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                  <h1 className="text-lg mt-1 sm:text-xl md:text-2xl font-medium text-[#4A3F35] truncate">
                    {detail.title}
                  </h1>
                </div>
                <div>
                  <Link
                    to="/tarotCard"
                    onClick={handleBackClick}
                    className="inline-flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 rounded-lg border border-black/10 hover:bg-black/5 transition flex-shrink-0"
                    title={t("tarot.navigation.backToCategories")}
                  >
                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {detail.paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-sm sm:text-base text-[#4A3F35] leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-12 sm:mt-16">
              <TarotFanSpread
                onGetReading={handleGetReading}
                category={selectedCard!}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center bg-[#F5F1EA] min-h-screen px-4 sm:px-6">
          <div className="-mx-4 sm:-mx-6 pt-3 -mt-4 sm:-mt-6 w-[calc(100%+2rem)] sm:w-[calc(100%+3rem)]">
            <div
              className="relative backdrop-blur-xl border-b border-[#E8E3DA] shadow-[0_8px_24px_rgba(0,0,0,0.05)]"
              style={{ backgroundColor: "rgba(246, 241, 234, 0.78)" }}
            >
              <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#6D4C92] via-[#E5B8AD] to-[#2E7D5E]" />

              <div className="px-4 sm:px-6 py-3 sm:py-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-2">
                      <span className="text-base sm:text-lg">🔮</span>
                      <h1 className="text-sm sm:text-base font-semibold text-[#2D1B44] truncate">
                        {t("tarot.category.title")}
                      </h1>
                      <span className="hidden sm:inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#F8F4FF] border border-[#6D4C92]/20 text-[#6D4C92]">
                        {t("tarot.category.chooseOne")}
                      </span>
                    </div>
                    <p className="text-[11px] sm:text-xs text-[#6B6073] truncate mt-0.5">
                      {t("tarot.category.chooseReadingType")}
                    </p>
                  </div>

                  <button
                    onClick={handleMainBackClick}
                    className="inline-flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-white border border-black/10 shadow-sm hover:shadow-md hover:bg-black/[0.03] transition flex-shrink-0"
                    title={t("common.goBack")}
                  >
                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-[#2D1B44]" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Astrology Wheel */}
          <div className="relative w-full flex justify-center items-center mt-6 sm:mt-8">
            <div className="absolute w-64 h-64 sm:w-80 sm:h-80 lg:w-[420px] lg:h-[420px] bg-[#D4AF37]/18 blur-3xl rounded-full animate-pulse" />
            <div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-[420px] lg:h-[420px] rounded-full overflow-hidden drop-shadow-[0_0_30px_rgba(212,175,55,0.35)]">
              <img
                src="/AstrologyCircle.jpeg"
                alt={t("tarot.astrology.wheelAlt")}
                className="w-full h-full object-contain rounded-full animate-[spin_60s_linear_infinite]"
                draggable={false}
              />
            </div>
          </div>

          <div className="w-full max-w-[700px] mx-auto rounded-[24px] sm:rounded-[28px] p-5 sm:p-7 mb-6 sm:mb-8 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.12)] border border-[#E8E3DA]">
            <p className="text-center text-sm sm:text-base lg:text-lg leading-relaxed font-medium text-[#4A3F35]">
              ✨ {t("tarot.welcome")} ✨
            </p>
            <p className="text-center text-xs sm:text-sm text-[#8A7F73] mt-2">
              {t("tarot.quote")}
              <br />
            </p>
            <p className="text-center text-[10px] sm:text-xs text-[#8A7F73] mt-3 sm:mt-4 italic">
              {t("tarot.quoteFooter")}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8 mt-6 px-3">
            <div className="flex w-full max-w-[360px] sm:max-w-none bg-white border border-[#E5B8AD]/40 rounded-full shadow-lg overflow-hidden">
              {/* Soul Origins tab */}
              <button
                onClick={() => setActiveTab("soul")}
                className={`flex-1 flex items-center justify-center gap-1
                  px-2 py-1.5 sm:px-4 sm:py-2 lg:px-6 lg:py-4 rounded-full
                  text-[11px] sm:text-sm lg:text-lg font-medium lg:font-semibold
                  transition-all duration-300
                  ${
                    activeTab === "soul"
                      ? "bg-[#F6EBDD] text-purple-800 shadow-md"
                      : "text-[#2D1B44]"
                  }`}
              >
                <span className="text-base sm:text-lg lg:text-2xl">🍀</span>
                <span className="truncate">{t("tarot.tabs.soul")}</span>
              </button>

              {/* Current Energy tab */}
              <button
                onClick={() => setActiveTab("current")}
                className={`flex-1 flex items-center justify-center gap-1
                  px-2 py-1.5 sm:px-4 sm:py-2 lg:px-6 lg:py-4 rounded-full
                  text-[11px] sm:text-sm lg:text-lg font-medium lg:font-semibold
                  transition-all duration-300
                  ${
                    activeTab === "current"
                      ? "bg-[#F6EBDD] text-purple-800 shadow-md"
                      : "text-[#2D1B44]"
                  }`}
              >
                <span className="text-base sm:text-lg lg:text-2xl">⏳</span>
                <span className="truncate">{t("tarot.tabs.current")}</span>
              </button>
            </div>
          </div>

          {/* Cards Grid - Changes based on active tab */}
          <div className="w-full max-w-[900px] pb-8 sm:pb-12 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
              {activeTab === "soul" ? (
                // Soul Origins Tab - Show 4 original cards
                <>
                  {soulCategoryCards.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      className={cardClass}
                      onClick={() => handleCardClick(card.id)}
                    >
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#F6EBDD] text-[#2D1B44] border border-black/5">
                          {"emoji" in card ? (
                            <span className="text-lg leading-none">
                              {card.emoji}
                            </span>
                          ) : (
                            <card.Icon className="h-5 w-5" />
                          )}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-baseline gap-2">
                            <h3 className="font-semibold text-[#2D1B44] text-base sm:text-lg leading-tight">
                              {card.title}
                            </h3>
                            <span className="text-xs sm:text-sm text-[#8A7F73] truncate">
                              {card.subtitle}
                            </span>
                          </div>
                        </div>
                      </div>

                      <ul className="mt-4 pl-5 list-disc space-y-2 text-sm text-[#5D5565]">
                        {card.bullets.map((b) => (
                          <li key={b} className="leading-snug">
                            {b}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}

                  {false && (
                    <>
                      {/* LOVE Card */}
                      <div
                        className={`${cardClass} cursor-pointer group p-5 sm:p-8 text-center transition-all duration-500 transform hover:-translate-y-2`}
                        style={{
                          background: `linear-gradient(180deg, rgba(229,184,173,0.35) 0%, rgba(255,255,255,0.65) 45%, rgba(245,241,234,1) 100%)`,
                          border: "1px solid rgba(229,184,173,0.45)",
                          borderRadius: "24px",
                          position: "relative",
                          overflow: "hidden",
                        }}
                        onClick={() => handleCardClick("love")}
                      >
                        {/* GOLD METALLIC CORNER ORNAMENTS */}
                        <div
                          className="absolute top-0 left-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                        <div
                          className="absolute bottom-0 right-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none rotate-180"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />

                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(218,165,32,0.15),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                        <div className="relative z-10">
                          <div className="flex justify-center items-center mb-4">
                            <div className="text-4xl filter drop-shadow-[0_0_12px_rgba(253,230,138,0.4)] group-hover:scale-110 transition-transform duration-300">
                              ❤️
                            </div>
                          </div>
                          <h3 className="text-[#4A3F35] font-bold tracking-[0.2em] text-sm sm:text-base mb-1 transition-colors">
                            LOVE
                          </h3>
                          <p className="text-[#8A7F73] text-[10px] sm:text-xs mb-4 font-medium uppercase tracking-widest">
                            (ความรัก)
                          </p>
                          <ul className="text-[#5D5565] text-[11px] sm:text-sm space-y-2 font-light italic">
                            <li>• Heart opening</li>
                            <li>• Deep connections</li>
                            <li>• Self-love journey</li>
                          </ul>
                        </div>
                      </div>

                      {/* WORK Card */}
                      <div
                        className={`${cardClass} cursor-pointer group p-5 sm:p-8 text-center transition-all duration-500 transform hover:-translate-y-2`}
                        style={{
                          background: `linear-gradient(180deg, rgba(229,184,173,0.35) 0%, rgba(255,255,255,0.65) 45%, rgba(245,241,234,1) 100%)`,
                          border: "1px solid rgba(229,184,173,0.45)",
                          borderRadius: "24px",
                          position: "relative",
                          overflow: "hidden",
                        }}
                        onClick={() => handleCardClick("work")}
                      >
                        <div
                          className="absolute top-0 left-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                        <div
                          className="absolute bottom-0 right-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none rotate-180"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />

                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(218,165,32,0.15),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                        <div className="relative z-10">
                          <div className="flex justify-center items-center mb-4">
                            <div className="text-4xl filter drop-shadow-[0_0_12px_rgba(253,230,138,0.4)] group-hover:scale-110 transition-transform duration-300">
                              💼
                            </div>
                          </div>
                          <h3 className="text-[#4A3F35] font-bold tracking-[0.2em] text-sm sm:text-base mb-1 transition-colors">
                            WORK
                          </h3>
                          <p className="text-[#8A7F73] text-[10px] sm:text-xs mb-4 font-medium uppercase tracking-widest">
                            (การงาน)
                          </p>
                          <ul className="text-[#5D5565] text-[11px] sm:text-sm space-y-2 font-light italic">
                            <li>• Career alignment</li>
                            <li>• New opportunities</li>
                            <li>• Professional growth</li>
                          </ul>
                        </div>
                      </div>

                      {/* MONEY Card */}
                      <div
                        className={`${cardClass} cursor-pointer group p-5 sm:p-8 text-center transition-all duration-500 transform hover:-translate-y-2`}
                        style={{
                          background: `linear-gradient(180deg, rgba(229,184,173,0.35) 0%, rgba(255,255,255,0.65) 45%, rgba(245,241,234,1) 100%)`,
                          border: "1px solid rgba(229,184,173,0.45)",
                          borderRadius: "24px",
                          position: "relative",
                          overflow: "hidden",
                        }}
                        onClick={() => handleCardClick("money")}
                      >
                        <div
                          className="absolute top-0 left-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                        <div
                          className="absolute bottom-0 right-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none rotate-180"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />

                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(218,165,32,0.15),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                        <div className="relative z-10">
                          <div className="flex justify-center items-center mb-4">
                            <div className="text-4xl filter drop-shadow-[0_0_12px_rgba(253,230,138,0.4)] group-hover:scale-110 transition-transform duration-300">
                              💰
                            </div>
                          </div>
                          <h3 className="text-[#4A3F35] font-bold tracking-[0.2em] text-sm sm:text-base mb-1 transition-colors">
                            MONEY
                          </h3>
                          <p className="text-[#8A7F73] text-[10px] sm:text-xs mb-4 font-medium uppercase tracking-widest">
                            (การเงิน)
                          </p>
                          <ul className="text-[#5D5565] text-[11px] sm:text-sm space-y-2 font-light italic">
                            <li>• Abundance flow</li>
                            <li>• Financial stability</li>
                            <li>• Prosperity mindset</li>
                          </ul>
                        </div>
                      </div>

                      {/* HEALJAI Card */}
                      <div
                        className={`${cardClass} cursor-pointer group p-5 sm:p-8 text-center transition-all duration-500 transform hover:-translate-y-2`}
                        style={{
                          background: `linear-gradient(180deg, rgba(229,184,173,0.35) 0%, rgba(255,255,255,0.65) 45%, rgba(245,241,234,1) 100%)`,
                          border: "1px solid rgba(229,184,173,0.45)",
                          borderRadius: "24px",
                          position: "relative",
                          overflow: "hidden",
                        }}
                        onClick={() => handleCardClick("healjai")}
                      >
                        <div
                          className="absolute top-0 left-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                        <div
                          className="absolute bottom-0 right-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none rotate-180"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />

                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(218,165,32,0.15),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                        <div className="relative z-10">
                          <div className="flex justify-center items-center mb-4">
                            <div className="text-4xl filter drop-shadow-[0_0_12px_rgba(253,230,138,0.4)] group-hover:scale-110 transition-transform duration-300">
                              🌿
                            </div>
                          </div>
                          <h3 className="text-[#4A3F35] font-bold tracking-[0.2em] text-sm sm:text-base mb-1 transition-colors">
                            ASTRIA
                          </h3>
                          <ul className="text-[#5D5565] text-[11px] sm:text-sm space-y-2 font-light italic">
                            <li>• Emotional healing</li>
                            <li>• Inner peace & calm</li>
                            <li>• Mind-body wellness</li>
                          </ul>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                // Current Energy Tab - Show 2 new dummy cards
                <>
                  {currentCategoryCards.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      className={cardClass}
                      onClick={() => handleCardClick(card.id)}
                    >
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#F6EBDD] text-[#2D1B44] border border-black/5">
                          {"emoji" in card ? (
                            <span className="text-lg leading-none">
                              {card.emoji}
                            </span>
                          ) : (
                            <card.Icon className="h-5 w-5" />
                          )}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-baseline gap-2">
                            <h3 className="font-semibold text-[#2D1B44] text-base sm:text-lg leading-tight">
                              {card.title}
                            </h3>
                            <span className="text-xs sm:text-sm text-[#8A7F73] truncate">
                              {card.subtitle}
                            </span>
                          </div>
                        </div>
                      </div>

                      <ul className="mt-4 pl-5 list-disc space-y-2 text-sm text-[#5D5565]">
                        {card.bullets.map((b) => (
                          <li key={b} className="leading-snug">
                            {b}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}

                  {false && (
                    <>
                      {/* Current Challenges Card */}
                      <div
                        className={`${cardClass} cursor-pointer group p-5 sm:p-8 text-center transition-all duration-500 transform hover:-translate-y-2 col-span-2 sm:col-span-1`}
                        style={{
                          background: `linear-gradient(180deg, rgba(229,184,173,0.35) 0%, rgba(255,255,255,0.65) 45%, rgba(245,241,234,1) 100%)`,
                          border: "1px solid rgba(229,184,173,0.45)",
                          borderRadius: "24px",
                          position: "relative",
                          overflow: "hidden",
                        }}
                        onClick={() => handleCardClick("challenges")}
                      >
                        {/* GOLD METALLIC CORNER ORNAMENTS */}
                        <div
                          className="absolute top-0 left-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                        <div
                          className="absolute bottom-0 right-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none rotate-180"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />

                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(218,165,32,0.15),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                        <div className="relative z-10">
                          <div className="flex justify-center items-center mb-4">
                            <div className="text-4xl filter drop-shadow-[0_0_12px_rgba(253,230,138,0.4)] group-hover:scale-110 transition-transform duration-300">
                              ⚡
                            </div>
                          </div>
                          <h3 className="text-[#4A3F35] font-bold tracking-[0.2em] text-sm sm:text-base mb-1 transition-colors">
                            GENERAL READING
                          </h3>
                          <p className="text-[#8A7F73] text-[10px] sm:text-xs mb-4 font-medium uppercase tracking-widest">
                            (อุปสรรคปัจจุบัน)
                          </p>
                          <ul className="text-[#5D5565] text-[11px] sm:text-sm space-y-2 font-light italic">
                            <li>• Identify obstacles</li>
                            <li>• Overcome resistance</li>
                            <li>• Find your strength</li>
                          </ul>
                        </div>
                      </div>

                      {/* Upcoming Opportunities Card */}
                      <div
                        className={`${cardClass} cursor-pointer group p-5 sm:p-8 text-center transition-all duration-500 transform hover:-translate-y-2 col-span-2 sm:col-span-1`}
                        style={{
                          background: `linear-gradient(180deg, rgba(229,184,173,0.35) 0%, rgba(255,255,255,0.65) 45%, rgba(245,241,234,1) 100%)`,
                          border: "1px solid rgba(229,184,173,0.45)",
                          borderRadius: "24px",
                          position: "relative",
                          overflow: "hidden",
                        }}
                        onClick={() => handleCardClick("opportunities")}
                      >
                        <div
                          className="absolute top-0 left-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                        <div
                          className="absolute bottom-0 right-0 w-20 h-20 sm:w-24 sm:h-24 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 pointer-events-none rotate-180"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='gold' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23FDE68A'/%3E%3Cstop offset='50%25' stop-color='%23DAA520'/%3E%3Cstop offset='100%25' stop-color='%23A16207'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M5 50C5 25.1472 25.1472 5 50 5M5 30C5 16.1929 16.1929 5 30 5M5 12V5H12' stroke='url(%23gold)' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M15 45C15 28.4315 28.4315 15 45 15' stroke='url(%23gold)' stroke-width='1' stroke-dasharray='4 4' opacity='0.4'/%3E%3C/svg%3E")`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                          }}
                        />

                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(218,165,32,0.15),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                        <div className="relative z-10">
                          <div className="flex justify-center items-center mb-4">
                            <div className="text-4xl filter drop-shadow-[0_0_12px_rgba(253,230,138,0.4)] group-hover:scale-110 transition-transform duration-300">
                              🔑
                            </div>
                          </div>
                          <h3 className="text-[#4A3F35] font-bold tracking-[0.2em] text-sm sm:text-base mb-1 transition-colors">
                            Current Energy Insights
                          </h3>
                          <p className="text-[#8A7F73] text-[10px] sm:text-xs mb-4 font-medium uppercase tracking-widest">
                            (ข้อมูลเชิงลึกด้านพลังงานในปัจจุบัน)
                          </p>
                          <ul className="text-[#5D5565] text-[11px] sm:text-sm space-y-2 font-light italic">
                            <li>• New paths opening</li>
                            <li>• Timing & alignment</li>
                            <li>• Prepare to receive</li>
                          </ul>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <BirthDetailsPopup
        isOpen={showBirthPopup}
        onClose={handleBirthPopupClose}
        onSave={handleBirthDetailsSave}
        category={pendingCardSelection || undefined}
      />

      {healJaiTalkCategory.map((cat) => (
        <HealJaiTalkButton
          key={cat.id}
          category={cat}
          label={t("astriaHome.talk.astria")}
        />
      ))}
    </>
  );
};

export default TarotCardCategory;
